import styled, { css } from 'styled-components'
import { Box } from '../../../shared_components/UI/Box'
import { H5, P, Strong } from '../../../shared_components/Library/Typeography'
import { ThemedButton } from '../../../shared_components/ThemedButton'
import { theme } from '../../../theme'
import { ReactNode } from 'react'

export const StakeCont = styled(Box)`
  --dotSize: 1px;

  background: var(--surface-brand, #FFF00);
  position: relative;

  ::after {
    /* Cover our element */
    content: '';
    position: absolute;
    inset: 0;
    opacity: 0.3;

    /* Dotted background */
    background-image: radial-gradient(
      circle at center,
      black var(--dotSize),
      transparent 0
    );
    background-size: 4px 4px;
    background-repeat: round;
    background-position: 0 0, 0.65rem 0.65rem;
    mask-image: linear-gradient(
      45deg,
      rgba(255, 255, 0, 0) 20.48%,
      #ff0 52.34%
    );

    z-index: 1;
  }

  > * {
    z-index: 2;
  }
`
export const StakeBox = styled(Box)`
  border-radius: var(--units-12, 12px);
  border: var(--units-1, 1px) solid var(--stroke-secondary, #333);
  background: var(--surface-primary, #080808);
  z-index: 2;
`
export const Divider = styled.div<{
  h?: string
  w?: string
  toHorizontalOnViewport?: number
}>`
  height: ${({ h }) => h};
  width: ${({ w }) => w};
  background: var(--stroke-tertiary, #252525);
  margin: auto;

  ${({ toHorizontalOnViewport }) =>
    toHorizontalOnViewport &&
    `
      @media screen and (max-width: ${toHorizontalOnViewport}px) {
        width: 80%;
        height: 1px;
      }
    `}
`
export const ResultsBox = styled(Box)`
  --dotSize: 0.5px;
  position: relative;

  ::after {
    /* Cover our element */
    content: '';
    position: absolute;
    inset: 0;
    opacity: 0.3;

    /* Dotted background */
    background-image: radial-gradient(
      circle at center,
      var(--surface-brand, #FFF00) var(--dotSize),
      transparent 0
    );
    background-size: 2px 2px;
    background-repeat: round;
    background-position: 0 0, 0.65rem 0.65rem;
    mask-image: linear-gradient(45deg, rgba(8, 8, 8, 0) 20.85%, #080808 74.06%);

    z-index: 1;
  }

  > * {
    z-index: 2;
  }
`
export const VaultsApyBox = styled(Box)`
  @media screen and (max-width: 768px) {
    flex-direction: column;
  }
`

export function Result({
  value,
  valuePrefix,
  valueSuffix,
  highlight,
  highlightColor,
  title,
  description,
  onTipClick,
}: {
  value: number | string
  valuePrefix?: string
  valueSuffix?: string
  highlight?: boolean
  highlightColor?: 'error' | 'brandPrimary'
  title: string
  description: string
  onTipClick?: () => void
}) {
  return (
    <ResultBox
      dc
      g={6}
      padV={16}
      padH={12}
      highlight={highlight}
      highlightColor={highlightColor}
    >
      <Box g={10} ac>
        <H5
          s="L"
          fw="bolder"
          c={
            highlight
              ? `text-${highlightColor}`
              : value === 0
              ? 'text-error'
              : 'text-brandPrimary'
          }
          ff="Proxima Nova"
          uc
        >
          {valuePrefix ?? ''}
          {value}
          {valueSuffix ?? ''}
        </H5>
        {onTipClick && (
          <button onClick={onTipClick}>
            {highlightColor === 'error' ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="17"
                height="16"
                viewBox="0 0 17 16"
                fill="none"
              >
                <path
                  d="M8.49992 10.6666V7.99998M8.49992 5.33331H8.50659M15.1666 7.99998C15.1666 11.6819 12.1818 14.6667 8.49992 14.6667C4.81802 14.6667 1.83325 11.6819 1.83325 7.99998C1.83325 4.31808 4.81802 1.33331 8.49992 1.33331C12.1818 1.33331 15.1666 4.31808 15.1666 7.99998Z"
                  stroke="#FF5252"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="17"
                height="16"
                viewBox="0 0 17 16"
                fill="none"
              >
                <path
                  d="M6.55992 6C6.71666 5.55444 7.02602 5.17873 7.43322 4.93942C7.84043 4.7001 8.31918 4.61262 8.78471 4.69247C9.25023 4.77232 9.67247 5.01435 9.97664 5.37568C10.2808 5.73702 10.4473 6.19434 10.4466 6.66666C10.4466 8 8.44659 8.66666 8.44659 8.66666M8.49992 11.3333H8.50659M15.1666 8C15.1666 11.6819 12.1818 14.6667 8.49992 14.6667C4.81802 14.6667 1.83325 11.6819 1.83325 8C1.83325 4.3181 4.81802 1.33333 8.49992 1.33333C12.1818 1.33333 15.1666 4.3181 15.1666 8Z"
                  stroke="#FFFF00"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>
        )}
      </Box>
      <Strong s="L" fw="semibold" c="text-primary">
        {title}
      </Strong>
      <P s="S" fw="regular" c="text-secondary">
        {description}
      </P>
    </ResultBox>
  )
}

const ResultBox = styled(Box)<{
  highlight?: boolean
  highlightColor?: 'error' | 'brandPrimary'
}>`
  border-radius: var(--units-12, 12px);
  border: var(--units-1, 1px) solid var(--stroke-secondary, #333);
  background: var(--surface-primary, #080808);

  ${({ highlight, highlightColor }) =>
    highlight &&
    css`
      border: var(--units-1, 1px) solid
        ${theme.semantics.zap.text[highlightColor]};
    `}
`

export function ResultOutcome({
  title,
  value,
  valueSuffix,
  maxValue,
  outcomeAlert,
}: {
  title: string
  value: number | string
  valueSuffix?: string
  maxValue: number
  outcomeAlert?: ReactNode
}) {
  return (
    <Box dc g={16}>
      <Box dc g={8}>
        <Strong s="XL" c="text-primary" fw="semibold">
          {title}
        </Strong>
        <ResultOutcomeValueBox
          fu
          aw
          padV={12}
          padH={6}
          bgc={value === 0 ? 'text-error' : 'text-brandPrimary'}
        >
          <H5
            s="4XL"
            fw="bolder"
            c={value === 0 ? 'text-primary' : 'text-invert'}
            ff="Proxima Nova"
            uc
          >
            {value.toLocaleString()}
            {valueSuffix ?? ''}
          </H5>
        </ResultOutcomeValueBox>
      </Box>
      {outcomeAlert}
    </Box>
  )
}

const ResultOutcomeValueBox = styled(Box)`
  border-radius: var(--units-4, 4px);
  backdrop-filter: blur(40.83272171020508px);
`
export const ButtonsBox = styled(Box)`
  .recalc-btn {
    display: none;

    @media screen and (max-width: 1000px) {
      display: flex;
    }
  }
`

export function StakeButton({
  onClick,
  disabled,
  isTbc,
}: {
  onClick: () => void
  disabled?: boolean
  isTbc?: boolean
}) {
  return (
    <ThemedButton
      $styleType="primary"
      onClick={isTbc ? undefined : onClick}
      disabled={disabled || isTbc}
    >
      {isTbc ? 'Staking coming soon' : 'Confirm & stake'}
    </ThemedButton>
  )
}

export function ClaimButton({
  onClick,
  isTbc,
  label,
  disabled,
}: {
  onClick: () => void
  isTbc?: boolean
  label: string
  disabled?: boolean
}) {
  return (
    <ThemedButton
      $styleType="secondary-error"
      onClick={isTbc ? undefined : onClick}
      disabled={isTbc || disabled}
    >
      {label}
    </ThemedButton>
  )
}

export const AssetsCont = styled(Box)`
  position: absolute;
  top: 0;
  z-index: 5;

  .left-coin {
    position: absolute;
    top: -40px;
    width: 115px;
    height: 56px;
    right: calc(100% - 252px);
    z-index: 2;

    @media screen and (max-width: 768px) {
      right: calc(100% - 152px);
    }
  }

  .right-coin {
    top: -72px;
    position: absolute;
    left: calc(100% - 210px);
    z-index: 1;

    @media screen and (max-width: 768px) {
      left: calc(100% - 170px);
    }
  }
`
export const CalculateBox = styled(Box)`
  display: none;

  @media screen and (max-width: 1000px) {
    display: block;
    width: 100%;
    flex: unset;
  }
`
export const ClaimInput = styled.input`
  height: 44px;
  padding: 9px var(--units-12, 12px);
  display: flex;
  flex: 1 0 0;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: var(--units-8, 8px);
  color: var(--text-primary, #fff);

  /* Web/Text/M/Regular */
  font-family: var(--type-family-space-grotesk, 'Space Grotesk');
  font-size: var(--Text-M-M-size, 14px);
  font-style: normal;
  font-weight: var(--type-weight-Regular, 300);
  line-height: var(--Text-M-M-line-height, 18px); /* 128.571% */

  border-radius: var(--units-8, 8px);
  border: var(--units-1, 1px) solid var(--stroke-secondary, #333);
  background: var(--surface-invert-alpha-tertiary, rgba(0, 0, 0, 0.4));

  :focus-visible {
    outline: 1px solid ${theme.colors.function.error};
  }
`
